import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

const KEY = (id) => `ar.section.${id}`;
const isPhone = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

/**
 * A settings card whose body folds under a chevron. Remembers the choice per browser
 * (localStorage). Long screens open with the heavy sections folded on a phone; on desktop each
 * section keeps the default the page asks for.
 *
 *   <CollapsibleCard id="emergency.wording" icon={Icon} title="Message wording" summary="9 templates" defaultOpen mobileDefaultOpen={false}>
 */
export function CollapsibleCard({ id, icon: Icon, title, summary, right, defaultOpen = true, mobileDefaultOpen, children, className = "", testid, tone = "", openSignal }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(KEY(id));
      if (saved === "1") return true;
      if (saved === "0") return false;
    } catch { /* private mode */ }
    return isPhone() && mobileDefaultOpen !== undefined ? mobileDefaultOpen : defaultOpen;
  });
  useEffect(() => { try { localStorage.setItem(KEY(id), open ? "1" : "0"); } catch { /* ignore */ } }, [id, open]);
  // A deep link ("Edit keywords" from Settings) can unfold a card the user had collapsed.
  useEffect(() => { if (openSignal) setOpen(true); }, [openSignal]);
  return (
    <section data-testid={testid || `section-${id}`} data-open={open ? "1" : "0"} className={`rounded-2xl border border-sand-200 bg-white ${tone} ${className}`}>
      {/* A div, not a <button>: pages wrap read-only sections in <fieldset disabled>, and a real
          button inside one could never be clicked — board members must still be able to unfold. */}
      <div role="button" tabIndex={0} data-testid={`${testid || `section-${id}`}-toggle`} aria-expanded={open}
        onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
        className={`flex w-full cursor-pointer select-none items-center gap-2 px-5 py-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-redline-500/30 ${open ? "border-b border-sand-100" : ""}`}>
        {Icon && <Icon className="h-4 w-4 shrink-0 text-redline-500" />}
        <h2 className="text-sm font-bold text-ink">{title}</h2>
        {summary && <span className="ml-auto hidden min-w-0 truncate text-xs text-ink/50 sm:inline" data-testid={`${testid || `section-${id}`}-summary`}>{summary}</span>}
        {right && <span className={summary ? "ml-auto sm:ml-2" : "ml-auto"} onClick={(e) => e.stopPropagation()}>{right}</span>}
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink/40 transition-transform duration-200 ${right ? "ml-2" : summary ? "ml-auto sm:ml-2" : "ml-auto"} ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <div className="px-5 pb-5">{children}</div>}
    </section>
  );
}

export default CollapsibleCard;
